// src/contexts/DataProvider.js
import React, { createContext, useEffect, useState, useCallback } from "react";
import { fetchAllHotlapEvents, fetchHotlapEventDetails } from "../services/hotlapService";
import { fetchGroupEvents, fetchGroupEventDetails } from "../services/groupEventService";
import { fetchDrivers } from "../services/driverService";
import { fetchVehicles } from "../services/vehicleService"; // Import the fetchVehicles function
import _ from "lodash";

export const DataContext = createContext();

export const DataProvider = ({ children }) => {
  const apiKey = process.env.REACT_APP_API_KEY;

  // API Call Counter
  // const [apiCallCount, setApiCallCount] = useState(0);

  // const incrementApiCallCount = useCallback(
  //   _.throttle(() => {
  //     setApiCallCount((prevCount) => {
  //       if (prevCount < 1000) {
  //         const newCount = prevCount + 1;
  //         console.log(`API Call Count: ${newCount}`);
  //         return newCount;
  //       } else {
  //         console.warn("API call limit reached (1000). Stopping further count.");
  //         return prevCount;
  //       }
  //     });
  //   }, 100),
  //   []
  // );

  // Initialize all state variables
  const [hotlapEvents, setHotlapEvents] = useState([]);
  const [hotlapEventDetails, setHotlapEventDetails] = useState({});
  const [groupEvents, setGroupEvents] = useState([]);
  const [selectedGroupEventDetails, setSelectedGroupEventDetails] = useState(null);
  const [groupSessions, setGroupSessions] = useState([]);
  const [championships, setChampionships] = useState([]);
  const [championshipDetails, setChampionshipDetails] = useState(null);
  const [leagueResults, setLeagueResults] = useState([]);
  const [circuits, setCircuits] = useState([]);
  const [selectedCircuit, setSelectedCircuit] = useState(null);
  const [vehicleRecords, setVehicleRecords] = useState([]);
  const [customerRecords, setCustomerRecords] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicleDetails, setVehicleDetails] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingChampionships, setLoadingChampionships] = useState(false);
  const [loadingDrivers, setLoadingDrivers] = useState(true);
  const [loadingVehicleRecords, setLoadingVehicleRecords] = useState(false);
  const [error, setError] = useState(null);

  // Fetch vehicles
  useEffect(() => {
    const loadVehicles = async () => {
      try {
        // incrementApiCallCount();
        const vehiclesData = await fetchVehicles();
        setVehicles(vehiclesData); // Set the vehicles data
      } catch (error) {
        console.error("Error loading vehicles:", error);
      }
    };
    loadVehicles();
  }, []);

  // Fetch drivers
  useEffect(() => {
    const loadDriverData = async () => {
      try {
        setLoadingDrivers(true);
        // incrementApiCallCount();
        const { data, error } = await fetchDrivers(apiKey);

        if (error) {
          setError(error === "Database overwhelmed" ? "Database overwhelmed, please try again shortly!" : "An unexpected error occurred, please try again.");
        } else {
          setDrivers(data);
          // console.log("Fetched drivers:", data); // Logging the driver data
        }
      } catch (err) {
        console.error("Error loading drivers:", err);
      } finally {
        setLoadingDrivers(false);
      }
    };
    if (drivers.length === 0) loadDriverData();
  }, [apiKey, drivers]); // Dependency

  // Fetch hotlap events and details
  useEffect(() => {
    const loadHotlapData = async () => {
      try {
        setLoading(true);
        const events = await fetchAllHotlapEvents();
        setHotlapEvents(events);

        // Fetch details and filter out invalid <result> entries
        const detailsPromises = events.map(async (event) => {
          const eventId = event.getElementsByTagName("id")[0]?.textContent;
          if (eventId) {
            const details = await fetchHotlapEventDetails(eventId);

            if (details) {
              const results = Array.from(details.getElementsByTagName("result"));

              // Filter invalid results
              const validResults = results.filter((result) => {
                const invalidTag = result.getElementsByTagName("invalid")[0];
                const isInvalid = invalidTag?.textContent.trim() === "1";
                return !isInvalid;
              });

              // Reconstruct the filtered <results> node
              const resultsParent = details.getElementsByTagName("results")[0];
              if (resultsParent) {
                while (resultsParent.firstChild) {
                  resultsParent.removeChild(resultsParent.firstChild);
                }
                validResults.forEach((result) => {
                  resultsParent.appendChild(result);
                });
              }

              return { [eventId]: details };
            }
          }
          return {};
        });

        const detailsArray = await Promise.all(detailsPromises);
        const detailsObject = Object.assign({}, ...detailsArray);
        setHotlapEventDetails(detailsObject);
      } catch (error) {
        console.error("Error loading hotlap data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (hotlapEvents.length === 0) loadHotlapData();
  }, [hotlapEvents]);

  useEffect(() => {
    if (groupEvents.length === 0) {
      setLoading(true);
      // incrementApiCallCount();
      fetchGroupEvents(apiKey)
        .then((data) => setGroupEvents(data))
        .catch((error) => console.error("Error fetching group events:", error))
        .finally(() => setLoading(false));
    }
  }, [apiKey, groupEvents]);

  useEffect(() => {
    if (championships.length === 0) {
      setLoadingChampionships(true);
      // incrementApiCallCount();
      fetch("https://api.simracing.co.uk/v0.1/championships", {
        headers: { Authorization: `SRL ${apiKey}` },
      })
        .then((response) => response.text())
        .then((data) => {
          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(data, "text/xml");
          const championshipsArray = Array.from(xmlDoc.getElementsByTagName("championship"));

          const championshipsData = championshipsArray.map((championship) => ({
            id: championship.getElementsByTagName("id")[0]?.textContent,
            name: championship.getElementsByTagName("name")[0]?.textContent,
          }));
          setChampionships(championshipsData);
        })
        .catch((error) => console.error("Error fetching championships:", error))
        .finally(() => setLoadingChampionships(false));
    }
  }, [apiKey, championships]);

  useEffect(() => {
    if (circuits.length === 0) {
      // incrementApiCallCount();
      fetch("https://api.simracing.co.uk/v0.1/circuits", {
        headers: { Authorization: `SRL ${apiKey}` },
      })
        .then((response) => response.text())
        .then((data) => {
          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(data, "text/xml");
          const circuitsArray = Array.from(xmlDoc.getElementsByTagName("circuit"));
          const circuitsData = circuitsArray.map((circuit) => ({
            id: circuit.getElementsByTagName("id")[0]?.textContent,
            name: circuit.getElementsByTagName("name")[0]?.textContent,
            vehicle_records_uri: circuit.getElementsByTagName("vehicle_records_uri")[0]?.textContent,
            customer_records_uri: circuit.getElementsByTagName("customer_records_uri")[0]?.textContent,
          }));
          setCircuits(circuitsData);
        })
        .catch((error) => console.error("Error fetching circuits:", error));
    }
  }, [apiKey, circuits]);

  const handleGroupEventSelection = (eventId) => {
    setLoading(true);
    // incrementApiCallCount();
    fetchGroupEventDetails(eventId, apiKey)
      .then((data) => {
        const { eventName, eventResults, sessions } = data;
        setSelectedGroupEventDetails({ eventName, eventResults });
        setGroupSessions(sessions);
      })
      .catch((error) => console.error("Error fetching event details:", error))
      .finally(() => setLoading(false));
  };

  const handleChampionshipSelection = (championshipId) => {
    setLoadingChampionships(true);
    // incrementApiCallCount();
    fetch(`https://api.simracing.co.uk/v0.1/championships/${championshipId}`, {
      headers: { Authorization: `SRL ${apiKey}` },
    })
      .then((response) => response.text())
      .then((data) => {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(data, "text/xml");
        const championshipName = xmlDoc.getElementsByTagName("name")[0]?.textContent || "";
        const results = Array.from(xmlDoc.getElementsByTagName("result"));

        const championshipResults = results.map((result) => {
          const eventsData = Array.from(result.getElementsByTagName("event")).map((event) => ({
            score: event.getElementsByTagName("score")[0]?.textContent || "-",
          }));

          while (eventsData.length < 6) {
            eventsData.push({ score: "-" });
          }

          return {
            position: result.getElementsByTagName("position")[0]?.textContent,
            customer_id: result.getElementsByTagName("customer_id")[0]?.textContent,
            customer_name: result.getElementsByTagName("customer_name")[0]?.textContent,
            score: result.getElementsByTagName("score")[0]?.textContent || "0",
            events: eventsData,
          };
        });

        setChampionshipDetails({
          championshipName,
          championshipResults: championshipResults.filter((result) => result.score !== "0"),
        });
      })
      .catch((error) => console.error("Error fetching championship details:", error))
      .finally(() => setLoadingChampionships(false));
  };

  const handleCircuitSelection = async (circuitId) => {
    const selected = circuits.find((circuit) => circuit.id === circuitId);
    setSelectedCircuit(selected);

    setLoadingVehicleRecords(true);
    try {
      // incrementApiCallCount();
      const vehicleResponse = await fetch(selected.vehicle_records_uri, {
        headers: { Authorization: `SRL ${apiKey}` },
      });
      const vehicleData = await vehicleResponse.text();
      const vehicleXml = new DOMParser().parseFromString(vehicleData, "text/xml");
      const vehicleRecordsData = Array.from(vehicleXml.getElementsByTagName("result")).map((result) => ({
        customer_id: result.getElementsByTagName("customer_id")[0]?.textContent,
        customer_name: result.getElementsByTagName("customer_name")[0]?.textContent,
        lap_time_str: result.getElementsByTagName("lap_time_str")[0]?.textContent,
        date: result.getElementsByTagName("date")[0]?.textContent,
        vehicle_name: result.getElementsByTagName("vehicle_name")[0]?.textContent,
        venue_name: result.getElementsByTagName("venue_name")[0]?.textContent,
        telemetry_uri: result.getElementsByTagName("telemetry_uri")[0]?.textContent,
      }));
      setVehicleRecords(vehicleRecordsData);

      // incrementApiCallCount();
      const customerResponse = await fetch(selected.customer_records_uri, {
        headers: { Authorization: `SRL ${apiKey}` },
      });
      const customerData = await customerResponse.text();
      const customerXml = new DOMParser().parseFromString(customerData, "text/xml");
      const customerRecordsData = Array.from(customerXml.getElementsByTagName("result")).map((result) => ({
        customer_id: result.getElementsByTagName("customer_id")[0]?.textContent,
        customer_name: result.getElementsByTagName("customer_name")[0]?.textContent,
        lap_time_str: result.getElementsByTagName("lap_time_str")[0]?.textContent,
        date: result.getElementsByTagName("date")[0]?.textContent,
        vehicle_name: result.getElementsByTagName("vehicle_name")[0]?.textContent,
        venue_name: result.getElementsByTagName("venue_name")[0]?.textContent,
        telemetry_uri: result.getElementsByTagName("telemetry_uri")[0]?.textContent,
      }));
      setCustomerRecords(customerRecordsData);
    } catch (error) {
      console.error("Error fetching circuit records:", error);
    } finally {
      setLoadingVehicleRecords(false);
    }
  };

  const handleVehicleSelection = async (vehicleId) => {
    setSelectedVehicle(vehicleId);
    setLoadingVehicleRecords(true);
    try {
      // incrementApiCallCount();
      const response = await fetch(`https://api.simracing.co.uk/v0.1/vehicles/${vehicleId}/circuit_records`, {
        headers: { Authorization: `SRL ${apiKey}` },
      });
      const data = await response.text();
      const vehicleXml = new DOMParser().parseFromString(data, "text/xml");
      const vehicleDetailsData = Array.from(vehicleXml.getElementsByTagName("result")).map((result) => ({
        date: result.getElementsByTagName("date")[0]?.textContent,
        customerId: result.getElementsByTagName("customer_id")[0]?.textContent,
        customerName: result.getElementsByTagName("customer_name")[0]?.textContent,
        lapTimeStr: result.getElementsByTagName("lap_time_str")[0]?.textContent,
        venueName: result.getElementsByTagName("venue_name")[0]?.textContent,
        circuitName: result.getElementsByTagName("circuit_name")[0]?.textContent,
      }));
      setVehicleDetails(vehicleDetailsData);
    } catch (error) {
      console.error("Error fetching vehicle details:", error);
    } finally {
      setLoadingVehicleRecords(false);
    }
  };

  return (
    <DataContext.Provider
      value={{
        hotlapEvents,
        hotlapEventDetails,
        groupEvents,
        selectedGroupEventDetails,
        groupSessions,
        championships,
        championshipDetails,
        leagueResults,
        circuits,
        selectedCircuit,
        vehicleRecords,
        customerRecords,
        vehicles,
        selectedVehicle,
        vehicleDetails,
        drivers,
        loading,
        loadingChampionships,
        loadingDrivers,
        loadingVehicleRecords,
        error,
        handleGroupEventSelection,
        handleChampionshipSelection,
        handleCircuitSelection,
        handleVehicleSelection,
        setLeagueResults,
        setSelectedCircuit,
        setVehicleRecords,
        setCustomerRecords,
        setSelectedVehicle,
        setVehicleDetails,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};
